import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { v7 as uuid } from 'uuid';
import { clsx as cx } from 'clsx';
import X from 'lucide-react/dist/esm/icons/x';
import useDebounce from '../../../hooks/use-debounce';
import { Input } from '~/components/ui/input';
import Icon from '../icon';
import { ClearButton, Container, SearchLabel } from './search-bar.css';

function SearchBar(props) {
	const {
		initial = '',
		currentValue,
		onInput,
		placeholder = 'Search',
		canClear = true,
		isDisabled = false,
		className,
		inputClassName,
		filterFront = false,
		inputRef = null,
		cyName = null,
		ariaLabel = 'Search',
		clearAriaLabel = 'Clear search'
	} = props;

	const [searchText, setSearchText] = useState(initial);

	const debouncedSearchText = useDebounce(searchText, 500);

	useEffect(() => {
		if( currentValue !== undefined && currentValue !== searchText ) {
			setSearchText(currentValue ?? '');
		}
	}, [currentValue]);

	useEffect(() => {
		if(searchText !== initial || filterFront || searchText === '') {
			onInput(searchText);
		}
	}, [debouncedSearchText]);

	const resetSearchText = () => {
		setSearchText('');
	};

	const resetOnEscape = (e) => {
		if( e.key !== 'Escape' ) {
			return;
		}

		if( !canClear ) {
			return;
		}

		resetSearchText();
		e.target.blur();
	};

	// Stable id for the component's lifetime — regenerating it every render
	// breaks the label↔input association and churns snapshots.
	const [id] = useState(() => uuid());
	const classes = cx('px-search-bar', className);
	const inputClasses = cx('mb-0 w-full sm:w-[22.5rem] pl-11 pr-10', inputClassName);

	return (
		<Container className={classes}>
			<Input
				id={id}
				type="text"
				value={searchText}
				placeholder={placeholder}
				aria-label={ariaLabel}
				disabled={isDisabled}
				onChange={(e) => setSearchText(e.target.value)}
				onKeyDown={resetOnEscape}
				data-testid="search-input"
				ref={inputRef}
				data-cy={cyName}
				className={inputClasses}
			/>

			<SearchLabel htmlFor={id}>
				<Icon use="search" color="silver-base" />
				<span className="sr-only">{placeholder}</span>
			</SearchLabel>

			{searchText && canClear && (
				<ClearButton
					type="button"
					aria-label={clearAriaLabel}
					data-testid="search-clear"
					onClick={resetSearchText}
				>
					<X className="h-4 w-4" aria-hidden="true" />
				</ClearButton>
			)}
		</Container>
	);
}

SearchBar.propTypes = {
	initial: PropTypes.string,
	currentValue: PropTypes.string,
	onInput: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
	canClear: PropTypes.bool,
	isDisabled: PropTypes.bool,
	filterFront: PropTypes.bool,
	className: PropTypes.string,
	inputClassName: PropTypes.string,
	inputRef: PropTypes.shape({ current: PropTypes.any }),
	cyName: PropTypes.string,
	ariaLabel: PropTypes.string,
	clearAriaLabel: PropTypes.string
};

export default SearchBar;
